// Shared click-binding for search result rows: legacy topbar dropdown,
// SMD-658 universal dropdown, and the full search results page.
//
// Preserves the SMD-439 side-panel integration ('sm-modal:open' with the
// showing-view-modal class), two-way messaging, and edit-modal behavior.
// Extracted verbatim from TopbarSearch (see git history of topbar_search.js).
//
// @see app/views/search/_showing_results.html.haml (legacy rows)
// @see app/views/search/universal/_showings.html.haml (universal rows)

import TwoWayMessaging from '../application/dashboard/two_way_messaging/two_way_messaging';

class SearchResultActions {
  #twoWayMessaging;

  // @param {Object} options
  // @param {Function} options.onBeforeOpen - called right before a modal/popup opens
  //   (the dropdown uses it to remember its open state for later restore)
  // @param {Function} options.onAfterOpen - called right after a modal/popup opens
  //   (the dropdown uses it to close itself)
  constructor({ onBeforeOpen = () => {}, onAfterOpen = () => {} } = {}) {
    this.onBeforeOpen = onBeforeOpen;
    this.onAfterOpen = onAfterOpen;

    this.#twoWayMessaging = new TwoWayMessaging();
    // Don't call init() - other scripts (e.g., dashboard.js) handle full initialization.
    // We only need the instance to call openMessagingWindow().
  }

  // Bind click handlers for result rows and their action buttons inside rootEl
  bind(rootEl) {
    // Edit buttons - open edit modal
    rootEl.querySelectorAll('.js-topbar-edit-btn').forEach(btn => {
      btn.addEventListener('click', this.handleEditClick.bind(this));
    });

    // Message buttons - open two-way messaging popup
    rootEl.querySelectorAll('.js-topbar-message-btn').forEach(btn => {
      btn.addEventListener('click', this.handleMessageClick.bind(this));
    });

    // Result item links - open view popup
    rootEl.querySelectorAll('.topbar-search__result').forEach(link => {
      link.addEventListener('click', this.handleResultClick.bind(this));
    });
  }

  // Open edit modal for the showing
  handleEditClick(e) {
    e.preventDefault();
    e.stopPropagation();
    const btn = e.currentTarget;
    this.openEditModal(btn.dataset.editUrl, btn.dataset.modalId);
  }

  // Open a plain edit modal (no showing-view styling) fetched from url. Used by
  // the showing edit button and by rows that edit in a modal rather than
  // navigating (residents/applicants — same modal as their list pages).
  openEditModal(url, modalId) {
    if (!url) {
      return;
    }

    this.onBeforeOpen();
    document.dispatchEvent(new CustomEvent('sm-modal:open', {
      detail: {
        url,
        modalId
      }
    }));
    this.onAfterOpen();
  }

  // Open two-way messaging popup for the showing
  handleMessageClick(e) {
    e.preventDefault();
    e.stopPropagation();
    const btn = e.currentTarget;
    const url = btn.dataset.messagingUrl;

    if (url) {
      this.onBeforeOpen();
      // TwoWayMessaging expects a link-like object with href property
      this.#twoWayMessaging.openMessagingWindow({ href: url });
      this.onAfterOpen();
    }
  }

  // Handle click on search result item - open view popup
  handleResultClick(e) {
    // Don't handle if clicking on action buttons (edit/message)
    if (e.target.closest('.topbar-search__result-actions')) {
      return;
    }

    const link = e.currentTarget;

    // Rows that edit in a modal (residents/applicants) open the same edit form
    // their list pages use, instead of navigating to it.
    if (link.dataset.editUrl) {
      e.preventDefault();
      this.openEditModal(link.dataset.editUrl, link.dataset.modalId);
      return;
    }

    // When the view-popup feature is disabled, the link has no view URL —
    // fall through to the default navigation (legacy behavior / listing rows).
    if (!link.dataset.viewUrl) {
      return;
    }

    e.preventDefault();
    this.openViewModal(link.dataset.viewUrl, link.dataset.modalId);
  }

  openViewModal(url, modalId) {
    if (!url) {
      return;
    }

    this.onBeforeOpen();
    document.dispatchEvent(new CustomEvent('sm-modal:open', {
      detail: {
        url,
        modalId,
        classes: 'showing-view-modal'
      }
    }));
    this.onAfterOpen();
  }
}

export default SearchResultActions;
